import { API } from './API'

const queryNames = {
  TOKEN: 'backend-token',
}

const TOKEN_VALIDITY_SECONDS = 120
const BACKEND_TOKEN_PATH = '/apitoken'

export type AuthHeader = Record<string, string>

type TokenRequest = {
  maxAgeSeconds: number
  scopes: string[]
  applicationId?: string
}

type CacheEntry = {
  validUntil: number
  header: AuthHeader
}

const tokenCache = new Map<string, CacheEntry>()
const promiseCache = new Map<string, Promise<AuthHeader>>()

// persist cache entry to localStorage
const saveToStorage = (key: string, entry: CacheEntry) => {
  localStorage.setItem(
    key,
    JSON.stringify({
      ...entry,
      savedAt: Date.now(),
    }),
  )
}

// load cache entry from localStorage
const loadFromStorage = (key: string): CacheEntry | null => {
  const raw = localStorage.getItem(key)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    if (parsed.validUntil >= Date.now()) {
      return { validUntil: parsed.validUntil, header: parsed.header }
    } else {
      localStorage.removeItem(key) // expired cleanup
    }
  } catch {
    return null
  }
  return null
}

const convertFrontTokenToBackendToken = async (scopes: string[], applicationId?: string): Promise<AuthHeader> => {
  const cacheKey = (applicationId ?? '') + scopes.join(' ')
  const now = Date.now()

  // Check in-memory cache
  const cacheEntry = tokenCache.get(cacheKey)
  if (cacheEntry && cacheEntry.validUntil >= now) {
    return cacheEntry.header
  }

  // Check localStorage cache
  const storageEntry = loadFromStorage(cacheKey)
  if (storageEntry) {
    tokenCache.set(cacheKey, storageEntry)
    return storageEntry.header
  }

  // If promise already in-flight, reuse it
  const existingPromise = promiseCache.get(cacheKey)
  if (existingPromise) {
    return existingPromise
  }

  // Fetch a new token
  const tokenPromise = (async (): Promise<AuthHeader> => {
    try {
      const req: TokenRequest = {
        maxAgeSeconds: TOKEN_VALIDITY_SECONDS,
        scopes,
        applicationId,
      }

      const resp = await API.post(`${BACKEND_TOKEN_PATH}`, {
        body: req,
      })

      const newCacheEntry: CacheEntry = {
        header: { Authorization: `Bearer ${resp.access_token}`, version: 'v2' },
        validUntil: now + 1000 * (TOKEN_VALIDITY_SECONDS - 10),
      }

      tokenCache.set(cacheKey, newCacheEntry)
      saveToStorage(cacheKey, newCacheEntry) // persist in local storage
      return newCacheEntry.header
    } finally {
      promiseCache.delete(cacheKey)
    }
  })()

  promiseCache.set(cacheKey, tokenPromise)
  return tokenPromise
}

export const BackendTokenAPI = {
  queryNames,
  convertFrontTokenToBackendToken,
}
